import { z } from "zod";
import type { ResponseOption, LocalizedText, QuestionResponseType } from "../../types/common";
import { emptyLocalizedText, primaryText } from "../../types/common";

export const scrollAndFlash = (domId: string, onDone?: () => void) => {
  requestAnimationFrame(() => {
    const el = document.getElementById(domId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("highlight-flash");
      setTimeout(() => {
        el.classList.remove("highlight-flash");
        onDone?.();
      }, 2000);
    } else {
      onDone?.();
    }
  });
};

export type EditorVariantChild = { id: number; feelId: string; text: LocalizedText; semanticKey?: string };
export type EditorVariant = {
  id: number;
  feelId: string;
  label: LocalizedText;
  semanticKey?: string;
  children?: EditorVariantChild[];
};

export const variantsToEditorTree = (variants: ResponseOption[]): EditorVariant[] => {
  const result: EditorVariant[] = [];
  for (const v of variants) {
    if (v.parent_label) {
      const existing = result.find(r => r.children && structurallyEqual(r.label, v.parent_label));
      if (existing?.children) {
        existing.children.push({ id: v.id, feelId: v.feel_id, text: v.text, semanticKey: v.semantic_key });
      } else {
        result.push({
          id: 0,
          feelId: "",
          label: v.parent_label,
          children: [{ id: v.id, feelId: v.feel_id, text: v.text, semanticKey: v.semantic_key }],
        });
      }
    } else {
      result.push({ id: v.id, feelId: v.feel_id, label: v.text, semanticKey: v.semantic_key });
    }
  }
  return result;
};

export const editorTreeToVariants = (tree: EditorVariant[]): ResponseOption[] =>
  tree.flatMap((node): ResponseOption[] =>
    node.children
      ? node.children.map(child => ({
          id: child.id,
          feel_id: child.feelId,
          text: child.text,
          parent_label: node.label,
          semantic_key: child.semanticKey,
        }))
      : [{ id: node.id, feel_id: node.feelId, text: node.label, parent_label: null, semantic_key: node.semanticKey }]
  );

export const emptyEditorVariant = (): EditorVariant => ({ id: 0, feelId: "", label: emptyLocalizedText() });

export const emptyEditorChild = (): EditorVariantChild => ({ id: 0, feelId: "", text: emptyLocalizedText() });

export const downloadJson = (data: unknown, filename: string) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const reorderArray = <T>(arr: ReadonlyArray<T>, from: number, to: number): T[] => {
  const result = [...arr];
  const [removed] = result.splice(from, 1);
  result.splice(to, 0, removed);
  return result;
};

const ErrorShape = z.object({ message: z.string() });

export const errorMessage = (err: unknown): string => {
  if (err instanceof Error) return err.message;
  const parsed = ErrorShape.safeParse(err);
  return parsed.success ? parsed.data.message : String(err);
};

export const INTERNAL_NAME_HINT = "Must start with lowercase letter, only lowercase letters, numbers, and underscores";

export const isValidInternalName = (name: string | undefined | null): boolean => {
  if (!name || name === "") return true;
  return /^[a-z][a-z0-9_]*$/.test(name);
};

export const isValidSemanticKey = isValidInternalName;

export const normalizeInternalName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/^[^a-z]+/, "");

export const structurallyEqual = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, i) => structurallyEqual(item, b[i]));
  }
  const ak = Object.keys(a as Record<string, unknown>);
  const bk = Object.keys(b as Record<string, unknown>);
  if (ak.length !== bk.length) return false;
  return ak.every(
    k =>
      Object.prototype.hasOwnProperty.call(b, k) &&
      structurallyEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])
  );
};

export const getResponseOptionTexts = (rt: QuestionResponseType | undefined): Record<string, string> => {
  if (!rt) return {};
  const map: Record<string, string> = {};
  const options = "variants" in rt ? rt.variants : "groups" in rt ? rt.groups.flatMap(g => g.variants) : [];
  for (const opt of options) {
    if (opt.feel_id) map[opt.feel_id] = primaryText(opt.text);
  }
  return map;
};
